import { useEffect, useRef, useCallback, useState } from 'react';
import WebSocketManager from './WebSocketManager';
import { MIN_CREDITS_TO_START } from '../../../constants/trialLimits';

/**
 * Custom React hook for WebSocket management
 * Provides clean interface with automatic cleanup and state management
 */
const useWebSocket = (config = {}) => {
  // WebSocket manager instance
  const wsManagerRef = useRef(null);
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Event listeners registry for cleanup
  const listenersRef = useRef(new Map());

  // Initialize WebSocket manager
  useEffect(() => {
    // Create WebSocket manager instance
    wsManagerRef.current = new WebSocketManager(config);

    // Set up connection callbacks
    wsManagerRef.current.onConnect = () => {
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
    };

    wsManagerRef.current.onDisconnect = (reason) => {
      setIsConnected(false);
      setIsConnecting(false);
      if (reason !== 'io client disconnect') {
        setError(`Disconnected: ${reason}`);
      }
    };

    wsManagerRef.current.onError = (err) => {
      setError(err.message || 'Connection error');
      setIsConnecting(false);
    };

    // Cleanup on unmount
    return () => {
      if (wsManagerRef.current) {
        wsManagerRef.current.destroy();
        wsManagerRef.current = null;
      }
      listenersRef.current.clear();
    };
  }, [config.url]); // Empty dependency array - only run once

  // Connect function: optionally run beforeConnect (e.g. credit-check API) before opening socket
  const connect = useCallback(async () => {
    if (!wsManagerRef.current) return false;

    setIsConnecting(true);
    setError(null);

    try {
      let usageData = null;
      if (typeof config.beforeConnect === 'function') {
        const result = await config.beforeConnect();
        if (result === false) {
          const msg = `Need at least ${MIN_CREDITS_TO_START} credits remaining to use Script Creator. Please upgrade.`;
          setError(msg);
          setIsConnecting(false);
          return { success: false, message: msg };
        }
        if (result && typeof result === 'object' && result.success === false) {
          const msg = result.message || 'Cannot connect with your current plan or credits.';
          setError(msg);
          setIsConnecting(false);
          return { success: false, message: msg };
        }
        if (result && typeof result === 'object' && result.usageData != null) {
          usageData = result.usageData;
        }
      }

      const success = await wsManagerRef.current.connect();
      if (!success) {
        const msg = 'Failed to connect to automation service. Please try again.';
        setError(msg);
        setIsConnecting(false);
        return { success: false, message: msg };
      }
      if (usageData && typeof config.onConnectWithUsage === 'function') {
        config.onConnectWithUsage(usageData);
      }
      return usageData ? { success: true, usageData } : true;
    } catch (err) {
      const msg = err?.message || 'Connection error';
      setError(msg);
      setIsConnecting(false);
      return { success: false, message: msg };
    }
  }, [config.url, config.beforeConnect, config.onConnectWithUsage]);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (wsManagerRef.current) {
      wsManagerRef.current.disconnect();
    }
    setIsConnected(false);
    setIsConnecting(false);
    setError(null);
  }, []);

  // Emit function with error handling
  const emit = useCallback((event, data, callback) => {
    if (!wsManagerRef.current) {
      const error = new Error('WebSocket not initialized');
      if (callback) callback(error);
      return false;
    }
    
    return wsManagerRef.current.emit(event, data, callback);
  }, []);

  // Add event listener with automatic cleanup
  const on = useCallback((event, callback) => {
    if (!wsManagerRef.current || typeof callback !== 'function') {
      console.warn('useWebSocket: Invalid event listener setup');
      return () => {};
    }

    // Wrap callback to handle errors
    const wrappedCallback = (...args) => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in WebSocket event handler for '${event}':`, error);
        setError(`Event handler error: ${error.message}`);
      }
    };

    // Add to manager
    wsManagerRef.current.on(event, wrappedCallback);

    // Track for cleanup
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event).add(wrappedCallback);

    // Return cleanup function
    return () => {
      if (wsManagerRef.current) {
        wsManagerRef.current.off(event, wrappedCallback);
      }
      if (listenersRef.current.has(event)) {
        listenersRef.current.get(event).delete(wrappedCallback);
        if (listenersRef.current.get(event).size === 0) {
          listenersRef.current.delete(event);
        }
      }
    };
  }, []);

  // Remove event listener
  const off = useCallback((event, callback) => {
    if (wsManagerRef.current) {
      wsManagerRef.current.off(event, callback);
    }
    if (listenersRef.current.has(event)) {
      listenersRef.current.get(event).delete(callback);
    }
  }, []);

  // Get connection status
  const getStatus = useCallback(() => {
    if (!wsManagerRef.current) {
      return {
        isConnected: false,
        isConnecting: false
      };
    }
    return wsManagerRef.current.getConnectionStatus();
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Connection state
    isConnected,
    isConnecting,
    error,
    
    // Connection methods
    connect,
    disconnect,
    
    // Communication methods
    emit,
    on,
    off,
    
    // Utility methods
    getStatus,
    clearError
  };
};

export default useWebSocket;
